import type { DicomJson } from '../dicomweb';

/**
 * DICOM VOI and Modality LUTs, and the window functions cornerstone-core 2.6.1 does not implement.
 *
 * cornerstone-core renders `viewport.voiLUT` whenever it is set and then ignores the window
 * (`viewport.voi`), so a W/L drag or preset on a mammogram that ships a VOI LUT Sequence changed the
 * numbers in the overlay and nothing on screen. Here the viewport's LUT is rebuilt from the window
 * just before every draw that changed it:
 * - an image with a VOI LUT keeps the LUT's shape and has it stretched and shifted so the image's own
 *   window ("anchor") maps onto the current one; at the anchor the original LUT is used untouched;
 * - VOILUTFunction SIGMOID and LINEAR_EXACT (0028,1056) become a table for the current window;
 * - an incomplete LUT (no data, or a descriptor that disagrees with it) is dropped instead of
 *   rendering a black image.
 *
 * Pure functions only: the cornerstone wiring lives in display/voilut-cs.ts.
 */

/** A LUT in the shape cornerstone-core reads from `viewport.voiLUT` / `viewport.modalityLUT`. */
export interface CsLut {
  id: string;
  firstValueMapped: number;
  numBitsPerEntry: number;
  lut: number[];
}

export interface VoiWindow {
  width: number;
  center: number;
}

export type VoiFunction = 'LINEAR' | 'LINEAR_EXACT' | 'SIGMOID';

const TAG = {
  descriptor: '00283002',
  data: '00283006',
  pixelRepresentation: '00280103',
  windowCenter: '00281050',
  windowWidth: '00281051',
  windowExplanation: '00281055',
  voiFunction: '00281056',
  voiLutSequence: '00283010',
  modalityLutSequence: '00283000',
} as const;

/** cornerstone indexes a LUT with `Math.max(...lut)`: past ~100k entries that overflows the stack. */
export const MAX_LUT_ENTRIES = 65536;

/** Output range every generated table is written in (cornerstone shifts the entries down to 8 bits). */
const OUTPUT_MAX = 255;

let lutSerial = 0;
function nextId(prefix: string): string {
  lutSerial += 1;
  return `${prefix}-${lutSerial}`;
}

function numbers(json: DicomJson | undefined, tag: string): number[] {
  const value = json?.[tag]?.Value;
  if (!Array.isArray(value)) return [];
  return value.map(Number).filter((n) => Number.isFinite(n));
}

function base64Words(base64: string, entries: number): number[] {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return lutWords(bytes, entries);
}

/** LUT Data bytes as entries: 16-bit little-endian words, or one byte per entry when that is what fits. */
export function lutWords(bytes: Uint8Array, entries: number): number[] {
  if (bytes.length === entries) return Array.from(bytes);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const words: number[] = [];
  for (let offset = 0; offset + 1 < bytes.length; offset += 2) words.push(view.getUint16(offset, true));
  return words;
}

/**
 * One LUT Sequence item, or undefined when it is incomplete: missing descriptor or data, fewer entries
 * than the descriptor declares, or an impossible bit depth.
 */
export function parseLutItem(item: DicomJson, signedInput: boolean, prefix: string): CsLut | undefined {
  const descriptor = numbers(item, TAG.descriptor);
  if (descriptor.length < 3) return undefined;
  const entries = descriptor[0] === 0 ? 65536 : descriptor[0];
  let first = descriptor[1];
  if (signedInput && first > 32767) first -= 65536;
  const bits = descriptor[2];
  if (!(bits >= 1 && bits <= 16) || entries <= 0) return undefined;
  const element = item[TAG.data] as { Value?: unknown[]; InlineBinary?: string } | undefined;
  let lut: number[] = [];
  if (Array.isArray(element?.Value)) lut = element.Value.map(Number);
  else if (typeof element?.InlineBinary === 'string') lut = base64Words(element.InlineBinary, entries);
  if (lut.length < entries || lut.slice(0, entries).some((v) => !Number.isFinite(v))) return undefined;
  return { id: nextId(prefix), firstValueMapped: first, numBitsPerEntry: bits, lut: lut.slice(0, entries) };
}

function sequenceItems(json: DicomJson | undefined, tag: string): DicomJson[] {
  const value = json?.[tag]?.Value;
  return Array.isArray(value) ? (value.filter((v) => v && typeof v === 'object') as DicomJson[]) : [];
}

/** The usable LUTs of a VOI or Modality LUT Sequence in WADO-RS metadata (incomplete items dropped). */
export function parseLutSequence(json: DicomJson | undefined, which: 'voi' | 'modality'): CsLut[] {
  const signed = numbers(json, TAG.pixelRepresentation)[0] === 1;
  const tag = which === 'voi' ? TAG.voiLutSequence : TAG.modalityLutSequence;
  return sequenceItems(json, tag)
    .map((item) => parseLutItem(item, signed, which))
    .filter((lut): lut is CsLut => lut !== undefined);
}

/** True when a LUT cornerstone would render is complete (c.f. parseLutItem). */
export function isUsableLut(lut: Partial<CsLut> | undefined): lut is CsLut {
  return !!lut && Array.isArray(lut.lut) && lut.lut.length > 0 && Number.isFinite(lut.firstValueMapped) && Number.isFinite(lut.numBitsPerEntry);
}

/** Window a VOI LUT stands for when the header has none: its whole input range. */
export function lutWindow(lut: CsLut): VoiWindow {
  return { width: lut.lut.length, center: lut.firstValueMapped + lut.lut.length / 2 };
}

export function parseVoiFunction(value: unknown): VoiFunction {
  const text = String(value ?? '').trim().toUpperCase();
  return text === 'SIGMOID' || text === 'LINEAR_EXACT' ? text : 'LINEAR';
}

export interface HeaderWindow extends VoiWindow {
  explanation: string;
}

/** Every window the header carries (WC/WW are multi-valued; explanation 0028,1055 names each). */
export function headerWindows(json: DicomJson | undefined): HeaderWindow[] {
  const centers = numbers(json, TAG.windowCenter);
  const widths = numbers(json, TAG.windowWidth);
  const explanations = (json?.[TAG.windowExplanation]?.Value ?? []) as unknown[];
  const count = Math.min(centers.length, widths.length);
  const windows: HeaderWindow[] = [];
  for (let i = 0; i < count; i++) {
    if (!(widths[i] > 0)) continue;
    windows.push({ center: centers[i], width: widths[i], explanation: String(explanations[i] ?? '').trim() });
  }
  return windows;
}

export function headerVoiFunction(json: DicomJson | undefined): VoiFunction {
  return parseVoiFunction(json?.[TAG.voiFunction]?.Value?.[0]);
}

/** Bits cornerstone assigns a LUT (it does not trust numBitsPerEntry), and the shift down to 8 bits. */
function eightBit(lut: number[]): (value: number) => number {
  let max = 0;
  for (const v of lut) if (v > max) max = v;
  const bits = max.toString(2).length;
  if (bits >= 8) return (value) => value >> (bits - 8);
  return (value) => (max > 0 ? Math.round((value * OUTPUT_MAX) / max) : 0);
}

/**
 * A table over every value the image can produce, [lo, hi], plus one sentinel entry of 255 at hi + 1
 * that no pixel reaches. The sentinel pins cornerstone's bit-depth guess at 8, so the entries are read
 * exactly as written. Undefined when the range is too wide for a table.
 */
function table(prefix: string, lo: number, hi: number, value: (input: number) => number): CsLut | undefined {
  const first = Math.floor(lo);
  const last = Math.ceil(hi);
  const entries = last - first + 1;
  if (!Number.isFinite(entries) || entries < 1 || entries + 1 > MAX_LUT_ENTRIES) return undefined;
  const lut = new Array<number>(entries + 1);
  for (let i = 0; i < entries; i++) lut[i] = Math.min(OUTPUT_MAX, Math.max(0, Math.round(value(first + i))));
  lut[entries] = OUTPUT_MAX;
  return { id: nextId(prefix), firstValueMapped: first, numBitsPerEntry: 8, lut };
}

/** Value range the VOI stage receives: the stored range through slope/intercept, or a modality LUT's output. */
export interface InputRange {
  lo: number;
  hi: number;
}

/**
 * The image's VOI LUT, stretched and shifted so the `anchor` window maps onto `current`
 * (what a W/L drag means on a LUT image: same curve, new width and level).
 */
export function stretchLut(original: CsLut, anchor: VoiWindow, current: VoiWindow, range: InputRange): CsLut | undefined {
  if (!(anchor.width > 0) || !(current.width > 0)) return undefined;
  const toEightBit = eightBit(original.lut);
  const lastIndex = original.lut.length - 1;
  const ratio = anchor.width / current.width;
  return table('voi-stretch', range.lo, range.hi, (input) => {
    const source = anchor.center + (input - current.center) * ratio;
    const index = Math.min(lastIndex, Math.max(0, Math.round(source - original.firstValueMapped)));
    return toEightBit(original.lut[index]);
  });
}

/** DICOM PS3.3 C.11.2.1.3: SIGMOID and LINEAR_EXACT as a table for one window. */
export function functionLut(fn: Exclude<VoiFunction, 'LINEAR'>, window: VoiWindow, range: InputRange): CsLut | undefined {
  if (!(window.width > 0)) return undefined;
  const { center, width } = window;
  if (fn === 'SIGMOID') {
    return table('voi-sigmoid', range.lo, range.hi, (x) => OUTPUT_MAX / (1 + Math.exp((-4 * (x - center)) / width)));
  }
  return table('voi-linear-exact', range.lo, range.hi, (x) => {
    if (x <= center - width / 2) return 0;
    if (x > center + width / 2) return OUTPUT_MAX;
    return ((x - center) / width + 0.5) * OUTPUT_MAX;
  });
}

export function sameWindow(a: VoiWindow, b: VoiWindow): boolean {
  return Math.abs(a.width - b.width) < 1e-6 && Math.abs(a.center - b.center) < 1e-6;
}

/** Window presets for the second and later header windows (the first is the image's default). */
export function extraHeaderWindows(centers: number[], widths: number[], explanations: string[]): Array<{ key: string; label: string; width: number; center: number }> {
  const presets: Array<{ key: string; label: string; width: number; center: number }> = [];
  for (let i = 1; i < Math.min(centers.length, widths.length); i++) {
    if (!(widths[i] > 0) || !Number.isFinite(centers[i])) continue;
    const explanation = (explanations[i] ?? '').trim();
    presets.push({ key: `dicom-${i + 1}`, label: explanation ? `DICOM: ${explanation}` : `Ventana DICOM ${i + 1}`, width: widths[i], center: centers[i] });
  }
  return presets;
}
